import get from 'lodash/get'
import moment from 'moment-timezone'
import { PWA_INSTALLED_KEY, STRAPI_USER_STORAGE_KEY } from '../constants'
import axios from 'axios'
import { Maybe, TaskEntity } from '../generated/graphql'
import { images } from '../theme'

/**
 * Gets JWT of authenticatedUser from either sessionStorage or localStorage
 */
export const fetchJwt = () => {
  const localUser = localStorage.getItem(STRAPI_USER_STORAGE_KEY)
  const sessionUser = sessionStorage.getItem(STRAPI_USER_STORAGE_KEY)
  const user = sessionUser || localUser

  return user ? JSON.parse(user).jwt : null
}

export function formatError({ response }: any) {
  return get(response, 'data.error.details[0].messages[0].message', 'An unknown error has occured.')
}
export const pathNameExists = (pathname: string, pathnames: string[]) => {
  return pathnames.some((path) => pathname.includes(path))
}

export const statusHelper = (status: string, hex?: boolean) => {
  const statusColors: { [key: string]: string } = {
    ACTIVE: hex ? '#82b79b' : 'green',
    INACTIVE: hex ? '#b77575' : 'red',
    IN_PROGRESS: hex ? '#bee3f8' : 'blue',
    REPORTED: hex ? '#bee3f8' : 'blue',
    COMPLETED: hex ? '#82b79b' : 'green',
    URGENT: hex ? '#b77575' : 'red',
    CANCELLED: hex ? '#b77575' : 'red',
    REJECTED: hex ? '#b77575' : 'red',
    PENDING: hex ? '#fefbbf' : 'orange',
    MORE_INFO_REQUESTED: hex ? '#9a88be' : 'purple',
    ARCHIVED: hex ? '#edf2f7' : 'red',
    LIVE: hex ? '#9a88be' : 'purple',
    SUBMITTED: hex ? '#fefbbf' : 'orange'
  }
  const icons: { [key: string]: string } = {
    ACTIVE: 'VscCircleFilled',
    INACTIVE: 'VscClose',
    IN_PROGRESS: 'VscIssueDraft',
    REPORTED: 'VscLoading',
    COMPLETED: 'VscCheck',
    URGENT: 'VscWarning',
    CANCELLED: 'VscClose',
    REJECTED: 'VscClose',
    PENDING: 'VscIssueDraft',
    MORE_INFO_REQUESTED: 'VscEdit',
    ARCHIVED: 'VscClose',
    LIVE: 'VscCircleFilled',
    SUBMITTED: 'VscCheck'
  }
  return {
    title: status.toUpperCase().replace('_', ' '),
    color: statusColors[status] || 'gray.500',
    icon: icons[status] || 'VscCircleFilled'
  }
}

/**
 * The label a collector sees for a task's status. Accepts the raw enum
 * (`MORE_INFO_REQUESTED`) and the badge's display title, which only replaces
 * the first underscore (`MORE INFO_REQUESTED`), so both spellings match.
 */
export const getStatusLabel = (status: string) => {
  switch ((status || '').toLowerCase().replace(/[\s_]+/g, '_')) {
    case 'pending':
      return 'SUBMITTED'
    case 'completed':
      return 'APPROVED'
    case 'rejected':
      return 'REJECTED'
    case 'more_info_requested':
      return 'REJECTED FOR EDIT'
    default:
      return status
  }
}

export const getImages = (task: Maybe<TaskEntity>) => {
  let hasImageField = false
  const evidence = task?.attributes?.evidences?.data?.[0]?.attributes

  const customFields = evidence?.custom_data_fields || []
  const fieldImages = []
  for (const field of customFields) {
    if (field?.__typename === 'ComponentCustomDataFieldsImageField') {
      const images = field?.images?.data
      if (images && images?.length > 0) {
        hasImageField = true
        fieldImages.push(...images)
      }
    }
  }

  return {
    hasImages: (evidence?.photos && evidence?.photos?.data?.length > 0) || hasImageField,
    images: (evidence?.photos?.data || []).concat(fieldImages)
  }
}
export function getImageByFileExtension(fileName: string) {
  const fileExtension = fileName?.split('.')?.pop()
  console.log(fileExtension, 'fileExtension')
  switch (fileExtension) {
    case 'csv':
      return images.csv
    case 'txt':
      return 'text.svg'
    case 'pdf':
      return images.pdf
    case 'doc':
    case 'docx':
      return images.word
    case 'xls':
    case 'xlsx':
      return images.excel
    case 'ppt':
    case 'pptx':
      return images.powerpoint
    case 'zip':
    case 'rar':
      return 'zip.svg'
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return 'image.svg'
    case 'mp3':
    case 'wav':
    case 'aac':
      return 'audio.svg'
    case 'mp4':
    case 'avi':
    case 'mkv':
      return 'video.svg'
    default:
      return images.logoNoText
  }
}

export const checkTime = (date: any) => {
  var currentTime = moment()
  var timeStore = moment(date)
  let minuteDiff = currentTime.diff(timeStore, 'minutes')
  let hourDiff = currentTime.diff(timeStore, 'h')
  let dayDiff = currentTime.diff(timeStore, 'd')
  let monthDiff = currentTime.diff(timeStore, 'm')
  let yearDiff = currentTime.diff(timeStore, 'y')
  if (minuteDiff < 60) {
    if (minuteDiff === 1) {
      return `${minuteDiff} minute ago`
    } else {
      return `${minuteDiff} minutes ago`
    }
  } else if (hourDiff < 25) {
    if (hourDiff === 1) {
      return `${hourDiff} hour ago`
    } else {
      return `${hourDiff} hours ago`
    }
  } else if (dayDiff < 32) {
    if (dayDiff === 1) {
      return `${dayDiff} day ago`
    } else {
      return `${dayDiff} days ago`
    }
  } else if (monthDiff < 12) {
    if (monthDiff === 1) {
      return `${monthDiff} month ago`
    } else {
      return `${monthDiff} months ago`
    }
  } else {
    if (yearDiff === 1) {
      return `${yearDiff} year ago`
    } else {
      return `${yearDiff} years ago`
    }
  }
}
export const openInMaps = (lat?: number, lng?: number) => {
  if (
    navigator.platform.indexOf('iPhone') !== -1 ||
    navigator.platform.indexOf('iPod') !== -1 ||
    navigator.platform.indexOf('iPad') !== -1
  )
    window.open(`maps://www.google.com/maps/place/${lat || 0},${lng || 0}`)
  else window.open(`https://www.google.com/maps/place/${lat || 0},${lng || 0}`)
}

export const PWAInstalledFlag = {
  getIsPWAInstalled: () => {
    return localStorage.getItem(PWA_INSTALLED_KEY)
  },
  setIsPWAInstalled: () => {
    localStorage.setItem(PWA_INSTALLED_KEY, 'true')
  }
}

export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
  unit: string
) => {
  if (lat1 === lat2 && lon1 === lon2) {
    return 0
  } else {
    var radlat1 = (Math.PI * lat1) / 180
    var radlat2 = (Math.PI * lat2) / 180
    var theta = lon1 - lon2
    var radtheta = (Math.PI * theta) / 180
    var dist =
      Math.sin(radlat1) * Math.sin(radlat2) +
      Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta)
    if (dist > 1) {
      dist = 1
    }
    dist = Math.acos(dist)
    dist = (dist * 180) / Math.PI
    dist = dist * 60 * 1.1515
    if (unit === 'K') {
      dist = dist * 1.609344
    }
    if (unit === 'N') {
      dist = dist * 0.8684
    }
    return Math.trunc(dist)
  }
}
export const randomColor = () => {
  //  exclude bright colors
  const letters = '0123456789ABCDEF'
  let color = '#'
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)]
  }
  return color
}
export function parseDot(str: string) {
  return str?.split('.').join('&dot;')
}

export const fetch = async (url: string, method = 'GET', _options?: any): Promise<any> => {
  if (method === 'GET') {
    const response = await axios.get(url).then((response) => {
      return response.data
    })
    return response
  } else {
    throw new Error('Method not supported')
  }
}
